/* Movatic WMS — Frontend | CesarFilho 932048016 */
import { useEffect, useRef, useState, FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuthStore } from '@/store/authStore'
import { useNavigate, useLocation } from 'react-router-dom'
import { login as apiLogin, fetchMe } from '@/lib/auth'
import { getBaseUrl } from '@/lib/http'
import { Boxes, User, Lock, ArrowRight, ShieldCheck } from 'lucide-react'
import { geoEquirectangular, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import worldTopo from '@/assets/world-110m.json'

const schema = z.object({
  username: z.string().min(1, 'Informe o usuário'),
  password: z.string().min(1, 'Informe a senha'),
})
type FormValues = z.infer<typeof schema>

/** Sanitização forte para leitores (Zebra/DataWedge) e teclados móveis. */
function cleanInput(s: string): string {
  if (!s) return s
  // 1) remove BOM, ASCII de controle (CR/LF/TAB/ESC…), DEL e
  //    espaços “esquisitos” (NBSP, ZWSP, separadores unicode, LRM/RLM, etc.)
  const STRANGE_SPACES =
    // eslint-disable-next-line no-control-regex -- intencional: remover chars de controlo
    /\uFEFF|[\u0000-\u001F\u007F]|\u00A0|\u1680|[\u2000-\u200F]|\u2028|\u2029|\u202F|\u205F|\u2060|\u3000|\u061C/g
  // 2) normaliza múltiplos espaços comuns para 1
  return s.replace(STRANGE_SPACES, '').replace(/\s+/g, ' ').trim()
}

/* ============================================================================
 * Camada visual (decorativa): mapa-múndi vetorial por projeção equirectangular
 * REAL (d3-geo). Fonte de verdade ÚNICA de coordenadas — o mapa (paths) e o nó
 * Portugal são projetados pela MESMA instância de projeção. Zero constante
 * calibrada a olho. Geometria = asset LOCAL versionado (world-atlas 110m em
 * src/assets/world-110m.json), importado em build — nenhum fetch de CDN em
 * runtime. Vetorial ⇒ nítido em qualquer resolução; Portugal alinhado por
 * construção em qualquer ecrã. É aria-hidden + pointer-events-none, logo NUNCA
 * interfere com a autenticação. (Pulsos de Portugal = F2, ver LoginPulses.)
 * ADR-0051 · F1 (mapa vetorial) + F2 (pulsos sobre a mesma projeção).
 * ========================================================================== */
const MAP_W = 1000
const MAP_H = 500
// Equirectangular: mundo inteiro à LARGURA (scale = W/2π), faixa de mundo
// centrada na vertical (translate no meio da caixa). O oceano = fundo do <svg>,
// que cobre também as margens do preserveAspectRatio — sem faixas mortas.
const projection = geoEquirectangular()
  .scale(MAP_W / (2 * Math.PI))
  .translate([MAP_W / 2, MAP_H / 2])
const toPath = geoPath(projection)
const worldCountries = feature(
  worldTopo as never,
  (worldTopo as never as { objects: { countries: never } }).objects.countries,
) as unknown as { features: unknown[] }
const COUNTRY_PATHS: string[] = worldCountries.features
  .map((f) => toPath(f as never) || '')
  .filter(Boolean)
// Portugal (Lisboa) = coordenada REAL projetada pela mesma projeção do mapa.
const PORTUGAL = projection([-9.14, 38.72]) as [number, number]

/** Mapa-múndi vetorial de fundo (decorativo, ADR-0051 F1). */
function LoginWorldMap() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 ${MAP_W} ${MAP_H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ background: '#05101f' }}
    >
      <defs>
        <filter id="pt-glow" x="-300%" y="-300%" width="700%" height="700%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g>
        {COUNTRY_PATHS.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#0d2038"
            stroke="rgba(90,160,224,0.28)"
            strokeWidth={0.5}
          />
        ))}
      </g>
      {/* Nó Portugal (âmbar) — projeção([-9.14, 38.72]), mesma projeção do mapa */}
      <g filter="url(#pt-glow)">
        <circle
          cx={PORTUGAL[0]}
          cy={PORTUGAL[1]}
          r={7}
          fill="none"
          stroke="rgba(239,159,39,0.45)"
          strokeWidth={1}
        />
        <circle cx={PORTUGAL[0]} cy={PORTUGAL[1]} r={2.6} fill="#ef9f27" />
      </g>
    </svg>
  )
}

// ~12 destinos decorativos (capitais/hubs) — cada um projetado pela MESMA
// projeção do mapa: projection([lon, lat]). Zero fração calibrada a olho.
const DEST_LONLAT: [number, number][] = [
  [-74.0, 40.7], // Nova Iorque
  [-122.4, 37.8], // São Francisco
  [-46.6, -23.5], // São Paulo
  [-58.4, -34.6], // Buenos Aires
  [-0.13, 51.5], // Londres
  [3.4, 6.5], // Lagos (África ocidental)
  [28.0, -26.2], // Joanesburgo
  [55.3, 25.2], // Dubai
  [72.8, 19.1], // Mumbai
  [116.4, 39.9], // Pequim
  [139.7, 35.7], // Tóquio
  [151.2, -33.9], // Sydney
]
// Pré-projetados p/ coordenadas do viewBox (mesma projeção do mapa e de Portugal).
const DEST_PTS: [number, number][] = DEST_LONLAT.map(
  (ll) => projection(ll) as [number, number],
)

/** easeInOutCubic — arranque/paragem suaves do pulso viajante. */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

/**
 * Pulsos Portugal → destinos, desenhados sobre a MESMA projeção e caixa do mapa
 * F1 (alinhamento por construção): reusa a instância `projection` de módulo e
 * replica a transformação "meet" do <svg>. Canvas decorativo, performance-first
 * (ADR-0051 Lei 4): requestAnimationFrame; prefers-reduced-motion → estático;
 * pausa em visibilitychange; cancelAnimationFrame no unmount; dpr ≤ 2.
 * aria-hidden + pointer-events-none — nunca interfere com a autenticação.
 */
function LoginPulses() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    // Transformação "meet" idêntica à do <svg> do mapa (mesma fórmula ⇒ mesma
    // caixa): scale mínimo p/ caber, faixa centrada. Recalculada no resize.
    let scale = 1
    let tx = 0
    let ty = 0
    let O: [number, number] = [0, 0] // Portugal em px-CSS
    let D: [number, number][] = [] // destinos em px-CSS
    const project = (p: [number, number]): [number, number] => [
      tx + p[0] * scale,
      ty + p[1] * scale,
    ]

    const resize = () => {
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      canvas.width = Math.max(1, Math.floor(w * dpr))
      canvas.height = Math.max(1, Math.floor(h * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0) // desenho em px-CSS
      scale = Math.min(w / MAP_W, h / MAP_H)
      tx = (w - MAP_W * scale) / 2
      ty = (h - MAP_H * scale) / 2
      O = project(PORTUGAL)
      D = DEST_PTS.map(project)
    }
    resize()

    // Ciclo (ms): viajar → onda de chegada → descanso. Escalonado por destino.
    const TRAVEL = 1500
    const ARRIVE = 900
    const CYCLE = 3600
    const stagger = CYCLE / DEST_PTS.length

    const drawBaseLine = (a: [number, number], b: [number, number]) => {
      ctx.strokeStyle = 'rgba(90,160,224,0.12)'
      ctx.lineWidth = 0.8
      ctx.beginPath()
      ctx.moveTo(a[0], a[1])
      ctx.lineTo(b[0], b[1])
      ctx.stroke()
    }

    const drawOrigin = (breathe: number) => {
      ctx.beginPath()
      ctx.arc(O[0], O[1], 7 + breathe * 4, 0, Math.PI * 2)
      ctx.strokeStyle = `rgba(239,159,39,${0.22 + 0.22 * breathe})`
      ctx.lineWidth = 1.4
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(O[0], O[1], 3.4, 0, Math.PI * 2)
      ctx.fillStyle = '#ef9f27'
      ctx.shadowColor = '#ef9f27'
      ctx.shadowBlur = 12
      ctx.fill()
      ctx.shadowBlur = 0
    }

    const drawStatic = () => {
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)
      for (const d of D) drawBaseLine(O, d)
      for (const d of D) {
        ctx.beginPath()
        ctx.arc(d[0], d[1], 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(150,200,255,0.85)'
        ctx.fill()
      }
      drawOrigin(0)
    }

    let raf = 0
    let t0 = 0
    const frame = (ts: number) => {
      if (!t0) t0 = ts
      const elapsed = ts - t0
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight)

      // linhas-base ténues sempre visíveis
      for (const d of D) drawBaseLine(O, d)

      for (let i = 0; i < D.length; i++) {
        const d = D[i]
        const phase = (elapsed + i * stagger) % CYCLE
        if (phase < TRAVEL) {
          // pulso a viajar Portugal → destino
          const t = easeInOutCubic(phase / TRAVEL)
          const cx = O[0] + (d[0] - O[0]) * t
          const cy = O[1] + (d[1] - O[1]) * t
          const grad = ctx.createLinearGradient(O[0], O[1], cx, cy)
          grad.addColorStop(0, 'rgba(55,138,221,0)')
          grad.addColorStop(1, 'rgba(90,160,224,0.55)')
          ctx.strokeStyle = grad
          ctx.lineWidth = 1.1
          ctx.beginPath()
          ctx.moveTo(O[0], O[1])
          ctx.lineTo(cx, cy)
          ctx.stroke()
          ctx.beginPath()
          ctx.arc(cx, cy, 2.1, 0, Math.PI * 2)
          ctx.fillStyle = 'rgba(150,200,255,0.95)'
          ctx.shadowColor = '#5aa0e0'
          ctx.shadowBlur = 8
          ctx.fill()
          ctx.shadowBlur = 0
        } else if (phase < TRAVEL + ARRIVE) {
          // onda de chegada: anel a expandir + esbater no destino
          const k = (phase - TRAVEL) / ARRIVE
          const a = Math.max(0, 1 - k)
          ctx.beginPath()
          ctx.arc(d[0], d[1], 3 + k * 20, 0, Math.PI * 2)
          ctx.strokeStyle = `rgba(90,160,224,${0.5 * a})`
          ctx.lineWidth = 1.3
          ctx.stroke()
          ctx.beginPath()
          ctx.arc(d[0], d[1], 2.1, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(150,200,255,${a})`
          ctx.fill()
        }
      }

      drawOrigin(0.5 + 0.5 * Math.sin(ts / 450)) // Portugal a "respirar"
      raf = requestAnimationFrame(frame)
    }

    const start = () => {
      if (reduce) {
        drawStatic()
        return
      }
      t0 = 0
      raf = requestAnimationFrame(frame)
    }
    const stop = () => cancelAnimationFrame(raf)

    const onVis = () => {
      if (document.hidden) stop()
      else start()
    }
    const onResize = () => {
      resize()
      if (reduce) drawStatic()
    }

    start()
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('resize', onResize)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  )
}

export default function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    setValue,
    getValues,
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)
  const navigate = useNavigate()
  const location = useLocation()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const usernameRef = useRef<HTMLInputElement | null>(null)

  const debugOn = new URLSearchParams(location.search).get('debug') === '1'

  useEffect(() => {
    usernameRef.current?.focus()
    console.log(
      '[Login] API base =',
      getBaseUrl() || '(relative /api via proxy)',
    )
  }, [])

  // Evita que o Enter do leitor "vaze" para listeners globais durante o login
  useEffect(() => {
    const stopEnterBubbling = (e: KeyboardEvent) => {
      if (e.key === 'Enter') e.stopPropagation() // mantém submit do form
    }
    window.addEventListener('keydown', stopEnterBubbling, { capture: true })
    return () =>
      window.removeEventListener('keydown', stopEnterBubbling, {
        capture: true,
      })
  }, [])

  // Bloqueia quebras de linha que alguns IMEs (ex.: DataWedge) tentam inserir
  const blockLineBreak = (
    e: FormEvent<HTMLInputElement> & { nativeEvent?: InputEvent },
  ) => {
    const ne = (e.nativeEvent || {}) as InputEvent
    if (
      ne?.inputType === 'insertLineBreak' ||
      ne?.inputType === 'insertParagraph'
    ) {
      e.preventDefault()
    }
  }

  // Impede vazamento do Enter nos inputs, mantendo submit do formulário
  const stopEnterPropagation = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.stopPropagation()
      // não usamos preventDefault aqui para permitir o submit normal do form
    }
  }

  // Sanitiza imediatamente ao colar
  const onPasteClean = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text')
    const cleaned = cleanInput(pasted)
    if (pasted !== cleaned) {
      e.preventDefault()
      const t = e.currentTarget
      const start = t.selectionStart ?? t.value.length
      const end = t.selectionEnd ?? t.value.length
      t.value = t.value.slice(0, start) + cleaned + t.value.slice(end)
      // Atualiza react-hook-form
      if (t.id.endsWith('username')) setValue('username', t.value)
      if (t.id.endsWith('password')) setValue('password', t.value)
    }
  }

  const onSubmit = async (raw: FormValues) => {
    setSubmitError(null)

    // Sanitiza de forma agressiva (resolve \r, \n, \t, ZWSP/NBSP, marks do Zebra)
    const username = cleanInput(raw.username)
    const password = cleanInput(raw.password)

    // Garante que o form reflita o valor limpo
    setValue('username', username, { shouldDirty: false })
    setValue('password', password, { shouldDirty: false })

    // Log de diagnóstico (abra DevTools; use ?debug=1 para painel visível)
    const codePoints = (s: string) => [...s].map((c) => c.charCodeAt(0))
    console.debug('[LOGIN submit]', {
      username,
      passwordLen: password.length,
      usernameCodes: codePoints(username),
      passwordCodes: codePoints(password),
    })

    try {
      // 1) autentica e guarda tokens (auth.ts já tenta JSON e x-www-form-urlencoded)
      const tokens = await apiLogin(username, password)
      const tokensForStore = {
        access: (tokens as any).access,
        refresh: (tokens as any).refresh ?? '',
      } as any
      setTokens(tokensForStore)

      // 2) busca usuário (se falhar, faz fallback mínimo e segue)
      try {
        const me = await fetchMe()
        const userForStore = {
          id: (me as any)?.id ?? 0,
          username: (me as any)?.username ?? username,
          ...(me as any),
        } as any
        setUser(userForStore)
      } catch (err: any) {
        console.warn(
          '[Login] fetchMe falhou, seguindo com tokens. Detalhe:',
          err?.message || err,
        )
        setUser({ id: 0, username } as any)
      }

      // 3) navega (SmartHome no router decide Desktop x Mobile)
      navigate('/', { replace: true })
    } catch (err: any) {
      const rawData = err?.response?.data
      const detail =
        rawData?.detail ||
        rawData?.message ||
        rawData?.error ||
        (typeof rawData === 'string' ? rawData : null)

      const msg =
        detail ||
        err?.message ||
        'Falha ao autenticar. Verifique usuário/senha e tente novamente.'

      setSubmitError(msg)
      setError('username', { message: '' })
      setError('password', { message: '' })

      // Loga completo p/ diagnóstico
      console.error('[Login] erro', {
        status: err?.response?.status,
        data: rawData,
      })
    }
  }

  const formValues = getValues() // para painel de debug

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#050a16]">
      {/* Mapa-múndi vetorial (fundo nítido por projeção real — ADR-0051 F1) */}
      <LoginWorldMap />
      {/* Gradiente de leitura à esquerda (para o card ler sobre o mapa) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, rgba(5,10,22,0.85) 0%, rgba(5,10,22,0.55) 28%, rgba(5,10,22,0) 60%)',
        }}
      />
      {/* Pulsos Portugal → destinos, sobre a mesma projeção do mapa (ADR-0051 F2) */}
      <LoginPulses />

      {/* Card de login (canto superior esquerdo) */}
      <div className="relative z-10 flex min-h-screen items-start justify-start p-6 sm:p-10">
        <div
          className="w-[400px] max-w-full rounded-2xl border p-7 shadow-2xl"
          style={{
            background: 'rgba(9,16,32,0.82)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderColor: 'rgba(55,138,221,0.35)',
          }}
        >
          {/* Logo + tagline */}
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl"
              style={{
                background: 'rgba(55,138,221,0.18)',
                border: '1px solid rgba(55,138,221,0.4)',
              }}
            >
              <Boxes size={22} style={{ color: '#5aa0e0' }} />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight text-white">
                Movatic WMS
              </h1>
              <p
                className="text-[11px] font-medium tracking-wide"
                style={{ color: '#5aa0e0' }}
              >
                Ecossistema MOVATIC_WMS
              </p>
            </div>
          </div>

          {debugOn && (
            <div className="mb-3 space-y-1 rounded border border-white/10 bg-black/30 p-2 text-xs text-white/70">
              <div>
                <b>API:</b> {getBaseUrl() || '(relative /api)'}
              </div>
              <div>
                <b>UA:</b> {navigator.userAgent}
              </div>
              <div>
                <b>username(clean):</b> “{cleanInput(formValues.username || '')}
                ”
              </div>
              <div>
                <b>password len(clean):</b>{' '}
                {cleanInput(formValues.password || '').length}
              </div>
            </div>
          )}

          {submitError && (
            <div className="mb-3 rounded-md border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-200">
              {submitError}
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.stopPropagation() // impede "enter extra" do leitor
            }}
          >
            <div>
              <label
                className="mb-1.5 block text-xs font-medium text-white/70"
                htmlFor="login-username"
              >
                Usuário
              </label>
              <div className="relative">
                <User
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                />
                <input
                  id="login-username"
                  className="w-full rounded-lg border border-white/10 bg-white/5 py-2.5 pl-10 pr-3 text-white placeholder-white/30 outline-none transition focus:border-[#378add] focus:bg-white/10"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="email"
                  placeholder="o seu utilizador"
                  {...register('username')}
                  onBeforeInput={blockLineBreak}
                  onKeyDown={stopEnterPropagation}
                  onPaste={onPasteClean}
                  onBlur={(e) =>
                    (e.currentTarget.value = cleanInput(e.currentTarget.value))
                  }
                  ref={(el) => {
                    ;(register('username').ref as any)?.(el)
                    usernameRef.current = el
                  }}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-300">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="mb-1.5 block text-xs font-medium text-white/70"
                htmlFor="login-password"
              >
                Senha
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                />
                <input
                  id="login-password"
                  className="w-full rounded-lg border border-white/10 bg-white/5 py-2.5 pl-10 pr-3 text-white placeholder-white/30 outline-none transition focus:border-[#378add] focus:bg-white/10"
                  type="password"
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="text"
                  placeholder="••••••••"
                  {...register('password')}
                  onBeforeInput={blockLineBreak}
                  onKeyDown={stopEnterPropagation}
                  onPaste={onPasteClean}
                  onBlur={(e) =>
                    (e.currentTarget.value = cleanInput(e.currentTarget.value))
                  }
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-300">
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              className="flex w-full items-center justify-center gap-2 rounded-lg py-2.5 font-semibold text-white shadow-lg transition hover:brightness-110 disabled:opacity-60"
              style={{
                background: '#378add',
                boxShadow: '0 8px 24px -8px rgba(55,138,221,0.7)',
              }}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Entrando...' : 'Entrar'}
              {!isSubmitting && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-white/45">
            <ShieldCheck size={13} />
            Acesso seguro · Movatic Consulting
          </div>
        </div>
      </div>

      {/* Origem · Portugal (canto inferior direito) */}
      <div
        className="absolute bottom-4 right-5 z-10 flex items-center gap-2 text-xs"
        style={{ color: 'rgba(255,255,255,0.75)' }}
      >
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: '#ef9f27', boxShadow: '0 0 8px #ef9f27' }}
        />
        Origem · Portugal
      </div>
    </div>
  )
}
